/**
 * The Terms of Service and Privacy Policy, in one place.
 *
 * This text previously lived inline in six components — two Cosmos pages, two
 * landing pages and two landing modals — which is six chances for a legal
 * document to disagree with itself. They all render from here now, so an
 * update is one edit.
 *
 * Source: "Terms of Service (ToS) — UniversityTech", effective 28 August 2026.
 */

export interface LegalBlock {
  heading?: string;
  paras?: string[];
  bullets?: string[];
  table?: { head: string[]; rows: string[][] };
}

export interface LegalSection {
  heading: string;
  paras?: string[];
  bullets?: string[];
  blocks?: LegalBlock[];
  table?: { head: string[]; rows: string[][] };
}

export interface LegalDoc {
  title: string;
  effectiveDate: string;
  operator: string;
  contact: string;
  grievanceOfficer: string;
  intro?: string[];
  sections: LegalSection[];
}

const OPERATOR =
  'UniversityTech.io is owned and operated by Paragise Private Limited ("we", "us", "our"), '
  + 'registered address: 1078, 10th Floor, WeWork, Building 4, Nesco IT Park, '
  + 'Western Express Highway, Goregaon (East), Mumbai, Maharashtra 400063, India.';
const CONTACT = 'info@universitytech.io';
const GRIEVANCE = 'Parag Agarwal, CEO & Co-Founder, parag@universitytech.io';
export const LEGAL_EFFECTIVE_DATE = '28th August 2026';

export const TERMS_OF_SERVICE: LegalDoc = {
  title: 'Terms of Service',
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  operator: OPERATOR,
  contact: CONTACT,
  grievanceOfficer: GRIEVANCE,
  sections: [
    {
      heading: '1. Acceptance of Terms',
      paras: ['By accessing or using UniversityTech.io, you agree to these Terms. If you do not agree, do not use the platform.'],
    },
    {
      heading: '2. Services Provided',
      paras: ['UniversityTech.io provides:'],
      bullets: [
        'Aggregated listings of publicly available university intellectual property (IP) offers.',
        'Researcher information limited to names supplied to us directly by universities under our institutional agreements.',
        'AI-generated professional and R&D summaries based on data provided and your own input as a user, where applicable.',
        'Search and matching tools for IP, researchers, and universities.',
        'Direct display of university contact details on listings, so you can reach out to a university yourself.',
        'An optional paid Concierge Service, where we contact a university on your behalf.',
      ],
      blocks: [{
        paras: [
          'Full platform access is provided on a paid subscription basis (see Section 6). There is no in-platform chat or inbox. All user-to-university contact happens either (a) directly, once you copy the university’s published contact email, or (b) via the Concierge Service.',
        ],
      }],
    },
    {
      heading: '3. Public and Institutional Data',
      paras: ['We display two categories of non-user data:'],
      bullets: [
        'University IP listings and general information, sourced from publicly available university and research-institution websites and databases.',
        'Researcher names, supplied to us directly by universities under Memoranda of Understanding (MoUs) we hold with most universities on the platform. We do not independently source researcher personal data (e.g., from LinkedIn or other social profiles).',
      ],
      blocks: [{
        paras: [
          'We make no claim of ownership over this data; all rights remain with the original owners. Any individual (user or not) may request removal by emailing info@universitytech.io. We will respond within 30 days.',
        ],
      }],
    },
    {
      heading: '4. Account Creation and Deletion',
      paras: [
        'You can create an account using your email address, or via Google Sign-In. When you use Google Sign-In, Google’s API returns only your email address to us, no other profile data is transferred.',
        'You may then optionally complete additional profile fields to improve personalisation of the recommendations we send you. All such fields are optional and self-declared by you; we do not source them from any third party.',
        'If you delete your account, your profile information will be deleted or anonymised within 30 days, except where retention is required by law.',
      ],
    },
    {
      heading: '5. Contacting Universities',
      bullets: [
        'For self-service contact, we display the university’s contact email directly on the relevant listing. You may copy this and contact the university independently; once you do, that communication is between you and the university and is no longer processed by us.',
        'If you’d prefer we make the introduction, you may subscribe to our paid Concierge Service (see Section 6).',
      ],
    },
    {
      heading: '6. Subscription, Fees, and Refunds',
      blocks: [
        {
          heading: '6.1 Voyager Plan',
          paras: ['Full access to the platform is available through the Voyager Plan, a paid subscription. You can avail full platform access by making the requisite payment through Razorpay; we do not store your full card or bank details ourselves — these are handled directly by Razorpay. Current pricing is available on the platform at the time of purchase.'],
        },
        {
          heading: '6.2 Concierge Service',
          paras: ['The Concierge Service is offered at a separate pricing that can be shared via email, separate from the Voyager Plan subscription.'],
        },
        {
          heading: '6.3 Invoicing',
          paras: ['A GST-compliant invoice will be issued for each payment made on the platform.'],
        },
        {
          heading: '6.4 Cancellation and Refunds',
          paras: ['If you request a cancellation or refund within 14 days of your purchase date, we will process it. Refunds follow standard processing timelines and will be returned to your original payment method within 5 to 7 working days (or up to 10–12 days, depending on your bank or payment channel).'],
        },
      ],
    },
    {
      heading: '7. Accuracy and AI Disclaimer',
      paras: [
        'Search results, AI summaries, and recommendations are generated by algorithms. We cannot guarantee 100% accuracy, timeliness, or completeness. You are responsible for verifying information before relying on it.',
        'If you are a researcher featured in an AI-generated summary and believe it is inaccurate, or you object to being featured, contact info@universitytech.io for correction or removal.',
      ],
    },
    {
      heading: '8. User Responsibilities',
      paras: ['You agree to:'],
      bullets: [
        'Use the platform lawfully and respectfully.',
        'Not misuse or attempt to disrupt our services.',
        'Not use university contact details obtained from listings for unsolicited bulk marketing or unlawful purposes.',
        'Provide accurate payment and billing information for your subscription.',
      ],
    },
    {
      heading: '9. Limitation of Liability',
      paras: ['We provide the service "as is" and disclaim liability for errors, omissions, or damages arising from use, to the maximum extent permitted by law. Nothing here limits liability that cannot lawfully be excluded, including certain data protection obligations.'],
    },
    {
      heading: '10. Governing Law',
      paras: ['These Terms are governed by the laws of India, with courts in Mumbai having exclusive jurisdiction, without prejudice to any mandatory consumer or data protection rights you have under the laws of your country of residence. If you are located in the EU/EEA, UK, or Switzerland, see the additional information in Section 11 of our Privacy Policy.'],
    },
  ],
};

export const PRIVACY_POLICY: LegalDoc = {
  title: 'Privacy Policy',
  effectiveDate: LEGAL_EFFECTIVE_DATE,
  operator: 'Paragise Private Limited, 1078, 10th Floor, WeWork, Building 4, Nesco IT Park, '
    + 'Western Express Highway, Goregaon (East), Mumbai, Maharashtra 400063, India.',
  contact: CONTACT,
  grievanceOfficer: GRIEVANCE,
  intro: [
    'This Privacy Policy is designed in accordance with India’s Digital Personal Data Protection Act, 2023 ("DPDP Act"). If you are located in the EU/EEA, UK, or Switzerland, additional rights and information specific to you are set out separately in Section 11.',
  ],
  sections: [
    {
      heading: '1. Data We Collect',
      blocks: [
        {
          heading: 'Account data',
          paras: ['Email address (from Google Sign-In, or self-provided at email signup).'],
        },
        {
          heading: 'Profile data (all fields optional, self-declared by you)',
          paras: [
            'When you complete your profile, you may choose to share: identity and contact details (such as industry); location details (city and country); an online profile link (e.g., LinkedIn); and information about your interests, persona, and R&D goals or problem statements, which help us personalise the recommendations and outreach we send you.',
            'If you share your contact number you agree to sign up for our WhatsApp campaign.',
          ],
        },
        {
          heading: 'University and researcher data',
          bullets: [
            'University IP listings: from publicly available university and research-institution websites and databases.',
            'Researcher names only: supplied directly by universities under our institutional MoUs.',
          ],
        },
        {
          heading: 'Payment and billing data',
          paras: ['Subscription plan selected, transaction ID, payment amount, invoice/GST details, and transaction status. Full card/bank details are processed and stored by Razorpay, our payment gateway, not by us directly.'],
        },
        {
          heading: 'Technical data',
          paras: ['IP address, device/browser information, cookies and similar technologies.'],
        },
      ],
    },
    {
      heading: '2. Purpose and Basis of Processing',
      paras: [
        'We process your personal data because you voluntarily provide it to us for a specified purpose, for example, completing your profile so we can personalise recommendations, or making a payment to access a paid plan. Under the DPDP Act, this is treated as deemed consent: where you have voluntarily shared data for a purpose and have not indicated otherwise, we may process it for that purpose. Where we rely on your consent (such as WhatsApp campaign targeting), you may withdraw it at any time, which may affect our ability to provide that specific feature.',
        'We use your data to:',
      ],
      bullets: [
        'Create and manage your account and subscription.',
        'Personalise recommendations and outreach to you.',
        'Provide IP listings, search results, and matchmaking.',
        'Operate the optional Concierge Service, where applicable.',
        'Run WhatsApp campaign targeting, only when you have shared your mobile number.',
        'Process payments, issue invoices, and handle refunds/cancellations.',
        'Analyse aggregated, de-identified usage for improving the platform.',
      ],
      blocks: [{ paras: ['There is no in-platform chat; chat/message data as a category does not apply.'] }],
    },
    {
      heading: '3. Data Sharing',
      paras: ['We do not sell your personal data. We share it only as follows:'],
      bullets: [
        'If you use the Concierge Service, relevant profile details and your message are shared with the specific university you’re engaging.',
        'Razorpay, our payment processor, for processing subscription and Concierge Service payments.',
        'Other service providers supporting our operations (e.g., cloud hosting, email/WhatsApp delivery, analytics), under contractual confidentiality and data protection obligations.',
        'Legal disclosures where required by law or valid legal process.',
      ],
      blocks: [{ paras: ['We do not share your profile with universities automatically; that only happens if you personally contact them (using the email we display) or opt into Concierge.'] }],
    },
    {
      heading: '4. Your Rights (Data Principal Rights under the DPDP Act)',
      paras: ['As a Data Principal, you have the right to:'],
      bullets: [
        'Access a summary of the personal data we hold about you and the processing activities carried out.',
        'Correction and updation of inaccurate or incomplete data.',
        'Erasure of your personal data, unless retention is required for a legal purpose.',
        'Grievance redressal — you may raise a grievance with our Grievance Officer (contact above), and we will respond within the timeframe prescribed by law.',
        'Nominate another individual to exercise these rights on your behalf in the event of your death or incapacity.',
        'Withdraw consent at any time where processing is based on your consent.',
      ],
      blocks: [{
        paras: [
          'To exercise any of these rights, email info@universitytech.io.',
          'If you are not satisfied with our response, you may approach the Data Protection Board of India.',
        ],
      }],
    },
    {
      heading: '5. Data Retention',
      table: {
        head: ['Data type', 'Retention period'],
        rows: [
          ['Researcher names (from university MoUs)', 'For the duration of the MoU, or until the university/researcher requests removal'],
          ['Publicly sourced university listings', 'Until removed from the source or a removal request is honoured'],
          ['Account and profile data', 'Until account deletion, then deleted/anonymised within 30 days, or upon withdrawal of consent'],
          ['Concierge Service communications', '24 months from engagement'],
          ['Payment/billing records', 'As required under applicable Indian tax and accounting law'],
        ],
      },
      blocks: [{ paras: ['We retain personal data only for as long as necessary for the purpose it was collected, or as required by law.'] }],
    },
    {
      heading: '6. Security and Breach Notification',
      paras: ['We use reasonable technical and organisational safeguards to protect your personal data. In the event of a personal data breach, we will notify the Data Protection Board of India and affected Data Principals as required under the DPDP Act, and take reasonable steps to mitigate the impact.'],
    },
    {
      heading: '7. Cross-Border Data Transfer',
      paras: ['As we are based in India, your data may be processed in India and, where relevant, transferred to service providers in other countries. Under the DPDP Act, such transfers are generally permitted except to countries specifically restricted by the Government of India.'],
    },
    {
      heading: '8. Automated Decision-Making and Profiling',
      paras: ['AI-generated summaries and matching involve automated processing but do not result in decisions with legal or similarly significant effects made solely by automated means. You may request human review or contest an output via info@universitytech.io.'],
    },
    {
      heading: '9. Children',
      paras: ['The platform is not intended for individuals under 18. We do not knowingly process data of anyone under this age without verifiable parental consent as required by the DPDP Act. If you believe a child’s data has been submitted to us, contact info@universitytech.io for prompt removal.'],
    },
    {
      heading: '10. Updates',
      paras: ['We may update this Privacy Policy. Material changes will be posted with an updated effective date, and all registered users will be notified via email in advance.'],
    },
    {
      heading: '11. Additional Information for Users in the EU/EEA, UK, and Switzerland',
      paras: [
        'This section applies only if you are located in the European Union, European Economic Area, United Kingdom, or Switzerland. It supplements, and does not replace, the rest of this Privacy Policy.',
        'Where GDPR (or UK GDPR, or the Swiss Federal Act on Data Protection) applies to our processing of your personal data because you are located in one of these regions, you additionally have:',
        'Legal bases for processing, mapped to the activities described above:',
      ],
      bullets: [
        'Providing your account/subscription: performance of a contract (Art. 6(1)(b) GDPR).',
        'Optional profile fields and personalisation: legitimate interests (Art. 6(1)(f)) — you voluntarily provide these fields so we can tailor recommendations to you, balanced against the low sensitivity of the data involved.',
        'WhatsApp campaign targeting: explicit, separate consent.',
        'University/researcher listing data: legitimate interests (Art. 6(1)(f)), balanced against the low sensitivity of institutional-affiliation data.',
        'Concierge Service: performance of a contract with you.',
      ],
      blocks: [{
        paras: [
          'Your rights include the right to access, rectify, erase, restrict, or object to processing; data portability; withdrawing consent at any time (where consent is the basis); and lodging a complaint with your local supervisory authority (e.g., the ICO in the UK, your national Data Protection Authority in the EU, or the FDPIC in Switzerland).',
          'International transfers: Since we are based in India, transfers of your data outside the EEA/UK/Switzerland rely on Standard Contractual Clauses (SCCs) (and the UK IDTA, where applicable). A copy is available on request.',
          'To exercise any of these rights, contact info@universitytech.io.',
        ],
      }],
    },
  ],
};
